import React, { PropsWithChildren, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { EnhancedOrderBox } from '../types/logistics';

type ReaderConfig = { boxes: EnhancedOrderBox[]; update: (box: EnhancedOrderBox | string) => void };

const ReaderContext = React.createContext<{
    started: boolean;
    updateStarted: (data: boolean) => void;
    config: ReaderConfig;
    updateConfig: (config: ReaderConfig) => void;
    messages: unknown[];
    updateMessages: (mode: string, data: unknown) => void;
    sounds: { ok: HTMLAudioElement; warn: HTMLAudioElement; error: HTMLAudioElement };
    ocrProcessing: boolean;
    updateOcrProcessing: (data: boolean) => void;
    cameraBlocked: boolean;
    updateCameraBlocked: (data: boolean) => void;
}>({
    started: false,
    updateStarted: () => {},
    config: { boxes: [] as EnhancedOrderBox[], update: () => {} },
    updateConfig: () => {},
    messages: [],
    updateMessages: () => {},
    sounds: {
        ok: new Audio('/ok.mp3'),
        warn: new Audio('/warn.mp3'),
        error: new Audio('/error.mp3')
    },
    ocrProcessing: false,
    updateOcrProcessing: () => {},
    cameraBlocked: false,
    updateCameraBlocked: () => {}
});

type readerProps = {};

export const ReaderContextProvider = (props: PropsWithChildren<readerProps>) => {
    const [started, setStarted] = useState(false);
    const [config, setConfig] = useState<ReaderConfig>({ boxes: [], update: () => {} });
    const [messages, setMessages] = useState<unknown[]>([]);
    const [ocrProcessing, setOcrProcessing] = useState(false);
    const [cameraBlocked, setCameraBlocked] = useState(false);

    const soundsRef = useRef({
        ok: new Audio('/ok.mp3'),
        warn: new Audio('/warn.mp3'),
        error: new Audio('/error.mp3')
    });

    const updateStarted = useCallback((data: boolean) => {
        setStarted(data);
    }, []);

    const updateConfig = useCallback((newConfig: ReaderConfig) => {
        setConfig(newConfig);
    }, []);

    const updateMessages = useCallback((mode:string, data:unknown) => {
        setMessages(() => {
            if(mode === 'add') return [data]; // Replace — only show the latest message
            if(mode === 'clear') return [];
            return [];
        });
    }, []);

    const updateOcrProcessing = useCallback((data: boolean) => {
        setOcrProcessing(data);
    }, []);

    const updateCameraBlocked = useCallback((data: boolean) => {
        setCameraBlocked(data);
    }, []);

    useEffect(() => {
        new AudioContext();
    }, []);

    const providerConfig = useMemo(() => ({
        started: started,
        updateStarted: updateStarted,
        config: config,
        updateConfig: updateConfig,
        messages: messages,
        updateMessages: updateMessages,
        sounds: soundsRef.current,
        ocrProcessing: ocrProcessing,
        updateOcrProcessing: updateOcrProcessing,
        cameraBlocked: cameraBlocked,
        updateCameraBlocked: updateCameraBlocked
    }), [started, updateStarted, config, updateConfig, messages, updateMessages, ocrProcessing, updateOcrProcessing, cameraBlocked, updateCameraBlocked]);

    return (
        <ReaderContext.Provider value={providerConfig}>
            {props.children}
        </ReaderContext.Provider>
    );
};

export default ReaderContext;